import { isRedirect, redirect } from "@tanstack/react-router"
import { createServerFn } from "@tanstack/react-start"
import { getRequestHeader } from "@tanstack/react-start/server"
import { env } from "cloudflare:workers"
import { z } from "zod"

import { validateTurnstile } from "@/cloudflare/turnstile"
import { logger } from "@/core/logger.server"
import { parseFormData } from "@/form"

import { getRedirectTarget, getRedirectUrl } from "../redirect"
import {
  isReviewEmail,
  consumeReviewLoginLimit,
  issueReviewOtp,
  matchesReviewLogin,
  reviewLoginConfig,
  type ReviewLoginLimiters,
} from "../review-login.server"
import { createSupabaseServerClient } from "../supabase.server"

export const loginFormSchema = z.object({
  email: z.email(),
  otp: z.string().optional(),
  redirectTo: z.string().optional(),
  "cf-turnstile-response": z.string().optional(),
})

export const getLoginPageData = createServerFn({ method: "GET" })
  .validator((data: { redirectTo: string }) => data)
  .handler(async ({ data }) => {
    return {
      cloudflareTurnstilePublicKey: env.CLOUDFLARE_TURNSTILE_PUBLIC_KEY,
      redirectTo: getRedirectUrl(data.redirectTo),
    }
  })

// Supabase Auth error codes for a wrong, used or expired one-time code
const INVALID_CODE_ERRORS = new Set(["otp_expired", "otp_disabled", "invalid_credentials"])

export const loginFormAction = createServerFn({ method: "POST" })
  .validator((data: FormData) => parseFormData(loginFormSchema, data))
  .handler(async ({ data }) => {
    const { email, otp, redirectTo, "cf-turnstile-response": turnstileResponse } = data

    const cloudflareTurnstileSecretKey = env.CLOUDFLARE_TURNSTILE_SECRET_KEY
    if (cloudflareTurnstileSecretKey) {
      const ip = getRequestHeader("CF-Connecting-IP")
      const isHuman = await validateTurnstile({
        secret: cloudflareTurnstileSecretKey,
        token: turnstileResponse,
        ip,
      })

      if (!isHuman) {
        throw new Error("Human verification failed")
      }
    }

    const supabase = createSupabaseServerClient()
    const review = reviewLoginConfig(env as unknown as Record<string, unknown>)

    if (!otp) {
      // Store reviewer account: no mail is sent; the answer is the same as for any email.
      if (isReviewEmail(review, email)) {
        return { email, success: "Check your email for a verification code" }
      }
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: true },
      })
      if (error) {
        logger.warn("auth.otp.send_failed", { error, code: error.code })
        throw new Error(error.message || "Failed to send an email", { cause: error })
      }

      return {
        email,
        success: "Check your email for a verification code",
      }
    }

    // The fixed 6-digit code is guessable, so every code attempt for the review email (right or
    // wrong) counts against the shared review-login limits first (T1268). Other emails never
    // touch them. Blocked and misconfigured both answer like a wrong code and never reach Supabase.
    if (isReviewEmail(review, email)) {
      try {
        const scope = await consumeReviewLoginLimit(
          env as unknown as ReviewLoginLimiters,
          getRequestHeader("CF-Connecting-IP") ?? "unknown",
        )
        if (scope) {
          logger.warn("auth.review_login.rate_limited", { scope })
          return { error: "Invalid verification code", email }
        }
      } catch (error) {
        logger.error("auth.review_login.limiter_unavailable", {
          reason: error instanceof Error ? error.message : "unknown",
        })
        return { error: "Invalid verification code", email }
      }
    }

    if (review && (await matchesReviewLogin(review, email, otp))) {
      try {
        const { data, error } = await supabase.auth.verifyOtp({
          email: review.email,
          token: await issueReviewOtp(review),
          type: "email",
        })
        if (error) throw error
        logger.info("auth.review_login.used", { user_id: data.user?.id })
        throw redirect(getRedirectTarget(redirectTo))
      } catch (error) {
        if (isRedirect(error)) throw error
        logger.error("auth.review_login.failed", { error })
        return { error: "Invalid verification code", email }
      }
    }
    if (isReviewEmail(review, email)) logger.warn("auth.review_login.bad_code")

    const { error } = await supabase.auth.verifyOtp({ email, token: otp, type: "email" })
    if (error) {
      logger.info("auth.otp.verify_failed", { code: error.code })
      return {
        error:
          error.code && !INVALID_CODE_ERRORS.has(error.code)
            ? error.message
            : "Invalid verification code",
        email,
      }
    }

    throw redirect(getRedirectTarget(redirectTo))
  })
